import React, { useEffect } from 'react';
import { Home } from 'lucide-react';
import { Card, CardHeader, CardFooter, AddButton, CollapsibleContent, EditModeToggle, EditModeCheckbox, EditModeActionBar } from '../shared';
import { InlineEditField } from '../shared/InlineEditField';
import { useTheme } from '../../contexts/ThemeContext';
import { useAuth } from '../../contexts/AuthContext';
import { useFinancialData } from '../../contexts/FinancialDataContext';
import { useInlineEdit } from '../../hooks/useInlineEdit';
import { useCollapsedWidgets } from '../../hooks/useCollapsedWidgets';
import { useEditMode } from '../../hooks/useEditMode';
import { formatCurrency } from '../../utils/formatters';
import { Asset } from '../../types';

interface AssetsWidgetProps {
  onAdd: () => void;
  variant?: 'card' | 'page';
}

export const AssetsWidget: React.FC<AssetsWidgetProps> = ({ onAdd, variant = 'card' }) => {
  const { darkMode, privacyMode } = useTheme();
  const { sheetId } = useAuth();
  const { assets, updateAsset, totalAssets, totalBanking, totalInvestmentValue, total401k, totalCrypto, totalPrivateEquity, handleDeleteAsset, handleBatchDelete } = useFinancialData();
  const { editValue, setEditValue, isEditing, startEdit, cancelEdit, completeEdit } = useInlineEdit();
  const { isCollapsed, toggleCollapse } = useCollapsedWidgets();
  const collapsed = isCollapsed('assets');

  const handleDeleteAsync = async (index: number) => {
    if (!sheetId) return;
    await handleDeleteAsset(index, sheetId);
  };

  const handleBatchDeleteAsync = async (indices: number[]) => {
    if (!sheetId) return;
    await handleBatchDelete('Assets', indices, sheetId);
  };

  const editMode = useEditMode(handleDeleteAsync, assets.length, handleBatchDeleteAsync);
  const { exitEditMode } = editMode;

  // Exit edit mode when widget collapses
  useEffect(() => {
    if (collapsed) exitEditMode();
  }, [collapsed, exitEditMode]);

  const handleSave = async (index: number, cell: string) => {
    const { value, isValid } = completeEdit();
    if (isValid && sheetId) {
      await updateAsset(index, value, sheetId);
    }
  };

  const isPage = variant === 'page';

  return (
    <Card variant={variant}>
      <CardHeader
        title="Assets"
        icon={<Home size={20} />}
        action={
          <div className="flex items-center gap-2">
            <EditModeToggle
              isEditMode={editMode.isEditMode}
              onToggle={editMode.toggleEditMode}
              itemCount={assets.length}
            />
            <AddButton onClick={onAdd} />
          </div>
        }
        collapsed={isPage ? false : collapsed}
        onToggleCollapse={isPage ? undefined : () => toggleCollapse('assets')}
        variant={variant}
      />

      <CollapsibleContent collapsed={isPage ? false : collapsed} variant={variant}>
        {editMode.isEditMode && (
          <EditModeActionBar
            selectedCount={editMode.selectedIndices.size}
            totalCount={assets.length}
            allSelected={editMode.selectedIndices.size === assets.length}
            onSelectAll={() => editMode.selectAll(assets.length)}
            onDeselectAll={editMode.deselectAll}
            onDeleteSelected={editMode.deleteSelected}
            isDeleting={editMode.isDeleting}
          />
        )}

        <div className="overflow-x-auto">
          {assets.length === 0 ? (
            <div className={`text-center py-6 ${darkMode ? 'text-slate-400' : 'text-slate-500'}`}>
              <p className="text-sm">No assets yet</p>
              <p className="text-xs mt-1">Add assets to track your net worth</p>
            </div>
          ) : (
            <table className="w-full text-sm">
              <thead>
                <tr className={`${darkMode ? 'text-slate-400' : 'text-slate-600'} border-b ${darkMode ? 'border-white/10' : 'border-slate-200'}`}>
                  {editMode.isEditMode && <th className="w-8 pb-2" />}
                  <th className="text-left pb-2">Asset</th>
                  <th className="text-right pb-2 w-[30%]">Value</th>
                </tr>
              </thead>
              <tbody>
                {/* Computed Cash Holdings row */}
                <tr className={`${darkMode ? 'text-slate-300' : 'text-slate-800'} border-b ${darkMode ? 'border-white/10' : 'border-slate-100'}`}>
                  {editMode.isEditMode && <td className="py-2 pr-2" />}
                  <td className="py-2 font-semibold">Cash Holdings</td>
                  <td className="text-right">
                    <div className="inline-block">
                      <div className={`font-bold ${darkMode ? 'text-green-400' : 'text-green-600'} pl-2 py-1 text-right w-24 md:w-28`}>
                        {formatCurrency(totalBanking, privacyMode)}
                      </div>
                    </div>
                  </td>
                </tr>
                {assets.map((asset: Asset, idx: number) => (
                  <tr
                    key={idx}
                    className={`${darkMode ? 'text-slate-300' : 'text-slate-800'} border-b ${darkMode ? 'border-white/10' : 'border-slate-100'}`}
                  >
                    {editMode.isEditMode && (
                      <td className="py-2 pr-2">
                        {asset.name !== 'Investment Portfolio' && (
                          <EditModeCheckbox
                            checked={editMode.selectedIndices.has(idx)}
                            onToggle={() => editMode.toggleIndex(idx)}
                          />
                        )}
                      </td>
                    )}
                    <td className="py-2 font-semibold">{asset.name}</td>
                    <td className="text-right">
                      <div className="inline-block">
                        <InlineEditField
                          value={asset.name === 'Investment Portfolio' ? totalInvestmentValue + total401k + totalCrypto + totalPrivateEquity : asset.value}
                          isEditing={isEditing('asset', idx, 'value')}
                          editValue={editValue}
                          onEditValueChange={setEditValue}
                          onStartEdit={() => startEdit('asset', idx, 'value', asset.value)}
                          onSave={() => handleSave(idx, asset.cell)}
                          onCancel={cancelEdit}
                          editable={asset.editable}
                          variant="positive"
                          privacyMode={privacyMode}
                        />
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>

      </CollapsibleContent>

      <CardFooter>
        <div className="flex justify-between items-center font-semibold text-sm">
          <span className={`${darkMode ? 'text-white' : 'text-slate-800'} flex-1`}>
            Total Assets
          </span>
          <span className={`${darkMode ? 'text-green-400' : 'text-green-600'} text-right`}>
            {formatCurrency(totalAssets, privacyMode)}
          </span>
        </div>
      </CardFooter>
    </Card>
  );
};
